import { Badge } from 'features/catalog/useBadges';
import {
  CONTRIBUTION_AWARD_THRESHOLDS,
  SYSTEM_AWARD_BADGE_IDS,
  getAutoUpdatedMap,
} from 'shared/hooks/getAutoUpdatedMap';
import { FeedUpdate } from 'features/feed/useFeedUpdates';
import { Profile, RawSubmission } from 'features/profiles/useProfiles';
import { BADGE_DESCRIPTIONS } from 'features/catalog/badge-definitions';
import { DEFAULT_BADGE_DATE_ADDED, isBadgeReleased, parseDateAdded } from 'features/catalog/badge-release';
import { formatLastUpdated } from 'config/constants';
import { getFirstPartOfEmail } from 'shared/lib/email';

export type BadgeOwnerClaim = {
  profileId: string;
  claimedAt: Date;
  /** True when feed wording for this obtain is “granted” (system / custom / deferred auto). */
  wasGranted: boolean;
};

/** Sheet rows for a profile id (email local-part), oldest first. */
export const submissionsForProfileId = (
  profileId: string,
  profile: Profile,
  submissionsByEmail: Map<string, RawSubmission[]>
): RawSubmission[] => {
  const direct = submissionsByEmail.get(profile.email);
  if (direct) {
    return [...direct].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  }
  const match = [...submissionsByEmail.entries()].find(([email]) => getFirstPartOfEmail(email) === profileId);
  return match ? [...match[1]].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime()) : [];
};

/** Pure grant badges — feed always says “granted”. Auto-claim milestones (Always Down,
 * Explorer, …) can fold into claim cards, so they are not grant-only. */
export const isBadgeGrantOnly = (
  badgeId: string,
  badge: Pick<Badge, 'banSelfClaim' | 'autoClaimRules'> | undefined
): boolean => {
  // Same rule as feed `isFeedGrantOnlyBadge`: milestones are claimable wording.
  if (badge?.banSelfClaim && badge.autoClaimRules?.ids.length) return false;
  return SYSTEM_AWARD_BADGE_IDS.has(badgeId) || !!badge?.banSelfClaim;
};

/**
 * Profile ids that received this badge as a feed grant. `'all'` when the badge itself
 * is grant-only (every owner uses Granted wording).
 */
export const buildGrantedOwnerIdsForBadge = (
  badgeId: string,
  badge: Pick<Badge, 'banSelfClaim' | 'autoClaimRules'> | undefined,
  feedUpdates: readonly FeedUpdate[]
): Set<string> | 'all' => {
  if (isBadgeGrantOnly(badgeId, badge)) return 'all';

  const ids = new Set<string>();
  for (const update of feedUpdates) {
    if (!update.isAutoGrant || update.isCatalogAddition) continue;
    if (!update.newBadges.includes(badgeId)) continue;
    const people = update.grantedToProfiles?.length ? update.grantedToProfiles : [update.profile];
    for (const person of people) {
      ids.add(getFirstPartOfEmail(person.email));
    }
  }
  return ids;
};

/** Earliest auto-grant `eventAt` per owner for this badge — same clock the feed card uses. */
export const buildGrantAtByOwnerId = (badgeId: string, feedUpdates: readonly FeedUpdate[]): Map<string, Date> => {
  const byOwner = new Map<string, Date>();
  for (const update of feedUpdates) {
    if (!update.isAutoGrant || update.isCatalogAddition) continue;
    if (!update.newBadges.includes(badgeId)) continue;
    const people = update.grantedToProfiles?.length ? update.grantedToProfiles : [update.profile];
    for (const person of people) {
      const id = getFirstPartOfEmail(person.email);
      const prev = byOwner.get(id);
      if (!prev || update.eventAt.getTime() < prev.getTime()) {
        byOwner.set(id, update.eventAt);
      }
    }
  }
  return byOwner;
};

export const formatBadgeObtainSubtext = (wasGranted: boolean, at: Date): string =>
  `${wasGranted ? 'Granted' : 'Claimed'} ${formatLastUpdated(at)}`;

/** Sorted catalog times for unique contributed badges (mirrors feed Badger / Baron math). */
const contributionCatalogTimesMs = (
  contributedBadgeIds: readonly string[],
  badgesMap: Map<string, Badge>
): number[] => {
  const times: number[] = [];
  const seen = new Set<string>();
  for (const id of contributedBadgeIds) {
    if (CONTRIBUTION_AWARD_THRESHOLDS[id] != null || seen.has(id) || !badgesMap.has(id)) continue;
    if (!isBadgeReleased(id)) continue;
    seen.add(id);
    times.push(parseDateAdded(BADGE_DESCRIPTIONS.get(id)?.dateAdded ?? DEFAULT_BADGE_DATE_ADDED).getTime());
  }
  times.sort((a, b) => a - b);
  return times;
};

/** Catalog-wave / progress grant stamp (never before join). */
const grantMsAtProgress = (criteriaMs: number, addedMs: number, joinMs: number): number => {
  const floor = Math.max(criteriaMs, addedMs, joinMs);
  return floor <= joinMs ? joinMs + 1 : floor;
};

/**
 * When this profile first obtained `badgeId` — sheet paste, auto-claim unlock,
 * contribution-award progress, or catalog stamp. Falls back to `lastUpdated`.
 */
export const getProfileBadgeClaimAt = (
  badgeId: string,
  profile: Profile,
  submissions: RawSubmission[],
  badgesMap: Map<string, Badge>
): Date => {
  const badge = badgesMap.get(badgeId);
  const addedMs = parseDateAdded(BADGE_DESCRIPTIONS.get(badgeId)?.dateAdded ?? DEFAULT_BADGE_DATE_ADDED).getTime();
  const canAutoClaim = !!badge?.autoClaimRules;
  const joinMs = submissions[0]?.timestamp.getTime() ?? profile.lastUpdated.getTime();

  for (const submission of submissions) {
    if (submission.badgeIDs.includes(badgeId)) return submission.timestamp;

    if (!canAutoClaim) continue;
    const atMs = submission.timestamp.getTime();
    if (atMs < addedMs) continue;

    const sheetOwned = new Map<string, boolean>();
    for (const id of submission.badgeIDs) sheetOwned.set(id, true);
    const enabled = getAutoUpdatedMap(sheetOwned, badgesMap, {
      email: profile.email,
      submittedBadgeIDs: profile.submittedBadgeIDs,
    });
    if (enabled.get(badgeId)) return submission.timestamp;
  }

  // Badger / Baron: stamp when the Nth contribution was catalogued (not lastUpdated).
  const contributionThreshold = CONTRIBUTION_AWARD_THRESHOLDS[badgeId];
  if (contributionThreshold != null && profile.badgesIDs.includes(badgeId)) {
    const contribTimes = contributionCatalogTimesMs(profile.submittedBadgeIDs, badgesMap);
    if (contribTimes.length >= contributionThreshold) {
      return new Date(grantMsAtProgress(contribTimes[contributionThreshold - 1], addedMs, joinMs));
    }
    return new Date(grantMsAtProgress(addedMs, addedMs, joinMs));
  }

  // Owned via auto-claim with no post-catalog submission — stamp at catalog drop.
  if (canAutoClaim && profile.badgesIDs.includes(badgeId)) {
    return new Date(addedMs);
  }

  // Other pure grants (banSelfClaim / system awards): catalog drop, or just after join.
  if (isBadgeGrantOnly(badgeId, badge) && profile.badgesIDs.includes(badgeId)) {
    return new Date(grantMsAtProgress(addedMs, addedMs, joinMs));
  }

  return profile.lastUpdated;
};

/** Owners sorted newest-claim first, with grant vs claim wording from the feed. */
export const orderBadgeOwnersByClaim = (
  ownerIds: readonly string[],
  badgeId: string,
  profilesMap: Map<string, Profile>,
  submissionsByEmail: Map<string, RawSubmission[]>,
  badgesMap: Map<string, Badge>,
  feedUpdates: readonly FeedUpdate[] = []
): BadgeOwnerClaim[] => {
  const grantedOwners = buildGrantedOwnerIdsForBadge(badgeId, badgesMap.get(badgeId), feedUpdates);
  // Prefer the feed card clock when present so modal subtext matches “granted … ago”.
  const grantAtByOwner = buildGrantAtByOwnerId(badgeId, feedUpdates);

  const rows = ownerIds.map((profileId) => {
    const profile = profilesMap.get(profileId);
    const fromFeed = grantAtByOwner.get(profileId);
    const claimedAt =
      fromFeed ??
      (profile
        ? getProfileBadgeClaimAt(
            badgeId,
            profile,
            submissionsForProfileId(profileId, profile, submissionsByEmail),
            badgesMap
          )
        : new Date(0));
    const wasGranted = grantedOwners === 'all' || grantedOwners.has(profileId);
    return { profileId, claimedAt, wasGranted };
  });

  rows.sort((a, b) => {
    const dt = b.claimedAt.getTime() - a.claimedAt.getTime();
    if (dt !== 0) return dt;
    return a.profileId.localeCompare(b.profileId);
  });

  return rows;
};
